import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { cn } from "@/shared/lib/cn";

const TAG_COLORS = [
  "#111827",
  "#4B5563",
  "#6B7280",
  "#9CA3AF",
  "#10B981",
  "#22C55E",
  "#84CC16",
  "#F59E0B",
  "#3B82F6",
  "#0EA5E9",
  "#06B6D4",
  "#6366F1",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#EF4444",
  "#DC2626",
  "#FBBF24",
  "#A855F7",
  "#14B8A6",
] as const;

const getColorClass = (color: string) => {
  const colorMap: Record<string, string> = {
    "#111827": "bg-[#111827]",
    "#4B5563": "bg-[#4B5563]",
    "#6B7280": "bg-[#6B7280]",
    "#9CA3AF": "bg-[#9CA3AF]",
    "#10B981": "bg-[#10B981]",
    "#22C55E": "bg-[#22C55E]",
    "#84CC16": "bg-[#84CC16]",
    "#F59E0B": "bg-[#F59E0B]",
    "#3B82F6": "bg-[#3B82F6]",
    "#0EA5E9": "bg-[#0EA5E9]",
    "#06B6D4": "bg-[#06B6D4]",
    "#6366F1": "bg-[#6366F1]",
    "#8B5CF6": "bg-[#8B5CF6]",
    "#EC4899": "bg-[#EC4899]",
    "#F97316": "bg-[#F97316]",
    "#EF4444": "bg-[#EF4444]",
    "#DC2626": "bg-[#DC2626]",
    "#FBBF24": "bg-[#FBBF24]",
    "#A855F7": "bg-[#A855F7]",
    "#14B8A6": "bg-[#14B8A6]",
  };
  return colorMap[color] || "bg-grid-200";
};

interface TagColorPaletteProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

export function TagColorPalette({
  value,
  onChange,
  disabled,
}: TagColorPaletteProps) {
  const [open, setOpen] = useState(false);

  const handleColorSelect = (color: string) => {
    if (disabled) return;
    onChange(color);
    setOpen(false);
  };

  return (
    <DropdownMenu
      open={open && !disabled}
      onOpenChange={disabled ? undefined : setOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "h-8 w-8 rounded-md border border-grid-200",
            value ? getColorClass(value) : "bg-grid-200",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          title={disabled ? "AI-generated tags cannot be edited" : "Edit color"}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-auto p-3">
        <div className="mb-2 text-xs font-medium text-ink-500">
          Choose a color
        </div>
        <div className="grid grid-cols-4 gap-2">
          {TAG_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={cn(
                "h-7 w-7 rounded-md border transition-shadow",
                getColorClass(color),
                value === color
                  ? "ring-2 ring-offset-2 ring-imperial-500 border-white"
                  : "border-transparent hover:ring-2 hover:ring-offset-2 hover:ring-grid-300"
              )}
              onClick={() => handleColorSelect(color)}
            />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
